import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import {
    Custom_Upgrade_Files_Insert_Input,
    GetCustomUpgradesDocument,
    In_Queue_Model_Variants_Enum,
    useCreateCustomUpgradeMutation
} from "generated/graphql";
import { GraphQLError } from "graphql";
import { segmentTrackCreateCustomUpgrade } from "in_queue/analytics";
import { useCluster } from "in_queue/contexts/ClusterContext";
import { useState } from "react";
import { useRequestCustomUpgradeValidation } from "./useRequestCustomUpgradeValidation";

const UNIQUE_NAME_CONSTRAINT_NAME =
    "custom_upgrades_team_cluster_name_active_unique";

export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024; // 5 MB

export enum AcceptedFileType {
    INCH = "inch",
    IDV = "idv"
}

/**
 * A single source file to attach to an upgrade. `variant` is null for a
 * one-file-for-all-models upgrade, or the model variant for a model-specific
 * file.
 */
export interface CustomUpgradeFileInput {
    readonly file: File;
    readonly fileType: AcceptedFileType;
    readonly variant: In_Queue_Model_Variants_Enum | null;
}

export interface CreateCustomUpgradeInput {
    readonly name: string;
    readonly notes?: string;
    readonly files: ReadonlyArray<CustomUpgradeFileInput>;
}

export type CreateCustomUpgradeResult =
    | { readonly ok: true }
    | { readonly ok: false; readonly message: string };

const matchesConstraint = (
    errors: readonly GraphQLError[],
    constraintName: string
): boolean => {
    return errors.some(
        (gqlError) =>
            gqlError.extensions?.code === "constraint-violation" &&
            gqlError.message.includes(constraintName)
    );
};

const parseApolloError = (
    errors: readonly GraphQLError[],
    name: string
): string => {
    const isPermissionDenied = errors.some(
        (gqlError) => gqlError.extensions?.code === "permission-error"
    );
    if (isPermissionDenied) {
        return "You don't have permission to add upgrades for this team.";
    }

    if (matchesConstraint(errors, UNIQUE_NAME_CONSTRAINT_NAME)) {
        return `An active upgrade "${name}" already exists for this cluster.`;
    }

    return `Failed to add upgrade: ${errors[0].message}`;
};

export interface UseCreateCustomUpgradeResult {
    readonly createUpgrade: (
        input: CreateCustomUpgradeInput
    ) => Promise<CreateCustomUpgradeResult>;
    readonly submitting: boolean;
}

export const useCreateCustomUpgrade = (): UseCreateCustomUpgradeResult => {
    const cluster = useCluster();
    const { teamId, auth0Id } = useUserDataContext();
    const { requestValidation } = useRequestCustomUpgradeValidation();

    const [readingFile, setReadingFile] = useState(false);
    const [createCustomUpgrade, { loading }] = useCreateCustomUpgradeMutation();

    const submitting = readingFile || loading;

    const createUpgrade = async (
        input: CreateCustomUpgradeInput
    ): Promise<CreateCustomUpgradeResult> => {
        if (input.files.length === 0) {
            return { ok: false, message: "No file selected" };
        }

        if (input.files.some(({ file }) => file.size > MAX_UPLOAD_BYTES)) {
            return {
                ok: false,
                message: "File is too large"
            };
        }

        let filesWithContents: ReadonlyArray<{
            readonly input: CustomUpgradeFileInput;
            readonly contents: string;
        }>;

        setReadingFile(true);
        try {
            filesWithContents = await Promise.all(
                input.files.map(async (fileInput) => ({
                    input: fileInput,
                    contents: await fileInput.file.text()
                }))
            );
        } catch {
            return {
                ok: false,
                message: "Failed to read file"
            };
        } finally {
            setReadingFile(false);
        }

        const customUpgradeFiles: Custom_Upgrade_Files_Insert_Input[] =
            filesWithContents.map(({ input: fileInput, contents }) => ({
                source_file_contents: contents,
                source_file_format: fileInput.fileType,
                source_file_name: fileInput.file.name,
                variant: fileInput.variant
            }));

        const primaryFile = filesWithContents[0];

        const notes = input.notes?.trim();

        try {
            const { data, errors } = await createCustomUpgrade({
                variables: {
                    clusterId: cluster.id,
                    teamId,
                    authId: auth0Id,
                    sourceFileContents: primaryFile.contents,
                    sourceFileFormat: primaryFile.input.fileType,
                    sourceFileName: primaryFile.input.file.name,
                    name: input.name,
                    notes: notes || null,
                    customUpgradeFiles
                },
                refetchQueries: [GetCustomUpgradesDocument],
                // The dialog closes on return, so the new upgrade has to be in
                // the list by then.
                awaitRefetchQueries: true,
                errorPolicy: "all"
            });

            if (!errors) {
                const createdId = data?.insert_custom_upgrades_one?.id;
                segmentTrackCreateCustomUpgrade({
                    cluster,
                    numFiles: input.files.length,
                    upgradeId: createdId
                });
                if (createdId) {
                    await requestValidation(createdId, "create");
                }
                return { ok: true };
            }

            return {
                ok: false,
                message: parseApolloError(errors, input.name)
            };
        } catch {
            return {
                ok: false,
                message: "Failed to add upgrade"
            };
        }
    };

    return { createUpgrade, submitting };
};
